import { BarChart3, Info, Target, Users, X } from "lucide-react";
import React, { useState } from "react";
import apiService from "../services/apiService";
import {
	calculateORFWithCustomPayload,
	convertLogicChainToORFPayload,
	convertSelectedSegmentsToORFPayload,
	validateORFPayload,
} from "./logicChainConverter";

// Updated API Service function using ApiService
export const calculateORF = async (segments = [], customPayload = null) => {
	try {
		// Check authentication first
		if (!apiService.isAuthenticated()) {
			throw new Error("Authentication required. Please sign in to continue.");
		}

		// Ensure valid token before making the request
		const hasValidToken = await apiService.ensureValidToken();
		if (!hasValidToken) {
			throw new Error("Unable to authenticate. Please sign in again.");
		}

		let requestBody;

		// Use custom payload if provided, otherwise build from segments
		if (customPayload) {
			// Validate the custom payload
			const validation = validateORFPayload(customPayload);
			if (!validation.valid) {
				throw new Error(`Invalid ORF payload: ${validation.error}`);
			}
			requestBody = customPayload;
		} else {
			// Build the default request payload with segments containing both id and calculatedReach
			requestBody = {
				expr: {
					op: "UNION",
					children: segments.map((segment) => ({
						id: segment.id,
						reach: segment.calculatedReach || segment.reach, // Fallback to reach if calculatedReach not available
					})),
				},
			};
		}

		console.log("ORF Request Payload:", JSON.stringify(requestBody, null, 2));

		// Use ApiService's fetchWithFallback instead of hardcoded fetch
		const response = await apiService.fetchWithFallback(
			"/segment/calculate-orf",
			{
				method: "POST",
				headers: {
					accept: "application/json",
					"Content-Type": "application/json",
				},
				body: JSON.stringify(requestBody),
			},
		);

		if (!response.ok) {
			// Handle specific error cases
			if (response.status === 401 || response.status === 403) {
				throw new Error("Authentication failed. Please sign in again.");
			}
			throw new Error(`API Error: ${response.status} - ${response.statusText}`);
		}

		const data = await response.json();
		console.log("ORF Response:", data);
		return data;
	} catch (error) {
		console.error("Error calculating ORF:", error);

		// Handle authentication errors specifically
		if (
			error.message.includes("Authentication") ||
			error.message.includes("sign in")
		) {
			// Trigger auth failure handling in ApiService
			apiService.handleAuthFailure(error);
		}

		throw error;
	}
};

// Utility function to format numbers - whole numbers except for decimals
const formatNumber = (num, forceDecimals = false) => {
	if (!num && num !== 0) return "0";

	if (forceDecimals) {
		return typeof num === "number" ? num.toFixed(5) : "0.00000";
	}

	return Math.round(num).toLocaleString();
};

// Utility function to format percentage
const formatPercentage = (num) => {
	if (num === null || num === undefined || num === 0) return "0%";
	return `${(num).toFixed(1)}%`;
};

// Tooltip component for additional metrics
const MetricsTooltip = ({ orf, stdDev, rawReach, children }) => {
	const [showTooltip, setShowTooltip] = useState(false);

	return (
		<div className="tooltip-container">
			<div
				onMouseEnter={() => setShowTooltip(true)}
				onMouseLeave={() => setShowTooltip(false)}
				className="tooltip-trigger"
			>
				{children}
				<Info className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600" />
			</div>
			{showTooltip && (
				<div className="tooltip-content">
					<div className="tooltip-item">
						<span className="tooltip-label">Raw Reach:</span>
						<span className="tooltip-value">{formatNumber(rawReach)}</span>
					</div>
					<div className="tooltip-item">
						<span className="tooltip-label">ORF:</span>
						<span className="tooltip-value">{formatNumber(orf, true)}</span>
					</div>
					<div className="tooltip-item">
						<span className="tooltip-label">Std Deviation:</span>
						<span className="tooltip-value">{formatNumber(stdDev, true)}</span>
					</div>
				</div>
			)}
		</div>
	);
};

// Main Campaign Analysis Panel Component
export const CampaignAnalysisPanel = ({
	isOpen,
	onClose,
	selectedSegments = [],
	selectedSets = [],
	segmentsData = {},
	cumulativeData = {},
	demographicData = {},
	logicChain = [],
	searchTerms = [],
	// Add filters prop to get demographic filter details
	filters = {},
}) => {
	const [analysisData, setAnalysisData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [authError, setAuthError] = useState(false);

	// Get current user from ApiService
	const currentUser = apiService.getUser();
	const isAuthenticated = apiService.isAuthenticated();

	const countSegmentsInPayload = (payload) => {
		const countInNode = (node) => {
			if (node.id) {
				return 1; // Leaf node (segment)
			} else if (node.children) {
				return node.children.reduce(
					(sum, child) => sum + countInNode(child),
					0,
				);
			}
			return 0;
		};

		return countInNode(payload.expr);
	};

	// Helper function to get top 5 segments for a specific demographic type
	const getTopSegmentsByType = (demographicType) => {
		const dataSource =
			Object.keys(demographicData).length > 0 ? demographicData : segmentsData;
		const key = `Demographic_${demographicType}`;

		if (!dataSource[key] || !dataSource[key].data) return [];

		return dataSource[key].data
			.filter(
				(segment) =>
					segment.segmentMetrics?.calculatedReach ||
					segment.segmentMetrics?.uniqueUsers,
			)
			.sort((a, b) => {
				const aReach =
					a.segmentMetrics?.calculatedReach ||
					a.segmentMetrics?.uniqueUsers ||
					0;
				const bReach =
					b.segmentMetrics?.calculatedReach ||
					b.segmentMetrics?.uniqueUsers ||
					0;
				return bReach - aReach;
			})
			.slice(0, 5)
			.map((segment) => ({
				name: segment.segmentVector?.segmentName || "Unknown",
				calculatedReach:
					segment.segmentMetrics?.calculatedReach ||
					segment.segmentMetrics?.uniqueUsers ||
					0,
			}));
	};

	const getTotalSegmentCount = (demographicType) => {
		const dataSource =
			Object.keys(demographicData).length > 0 ? demographicData : segmentsData;
		const key = `Demographic_${demographicType}`;

		if (!dataSource[key] || !dataSource[key].data) return 0;

		return dataSource[key].data.length;
	};

	// Helper function to find effective reach for a group of segments
	const findGroupEffectiveReach = (termSegments) => {
		if (
			!analysisData ||
			!analysisData.reachTree ||
			!analysisData.reachTree.children
		)
			return null;

		// Get segment IDs for this term
		const termSegmentIds = termSegments.map((seg) => seg.id);

		// Search through the reachTree children to find matching group
		for (const child of analysisData.reachTree.children) {
			if (child.operator === "UNION" && child.children) {
				// Check if this union contains segments from our term
				const childSegmentIds = child.children
					.filter((leaf) => leaf.operator === "LEAF" && leaf.id)
					.map((leaf) => leaf.id);

				// If any of our term's segments are in this union, it's our group
				const hasMatchingSegments = termSegmentIds.some((id) =>
					childSegmentIds.includes(id),
				);

				if (hasMatchingSegments) {
					return {
						effectiveReach: child.effectiveReach,
						rawReach: child.rawReach,
						orf: child.orf,
						stdDev: child.stdDev,
					};
				}
			}
		}
		return null;
	};

	// Helper function to format location display (show first part only)
	const formatLocationDisplay = (locationFilter) => {
		if (!locationFilter || !locationFilter.display) {
			return "Not Applied";
		}
		const locationString = locationFilter.display;
		// Check if it's actually a string
		if (typeof locationString !== "string") {
			console.log(
				"Location display is not a string:",
				typeof locationString,
				locationString,
			);
			return "Applied";
		}
		// Simple approach: just take first 30 characters and add "..."
		if (locationString.length > 30) {
			return `${locationString.substring(0, 30)}...`;
		}
		return locationString;
	};

	const buildLogicChainPayloadFromSelected = () => {
		console.log("🔧 Building logic chain payload from SELECTED segments only");
		console.log("📋 Input data:", {
			selectedSegments,
			logicChain,
			searchTerms,
		});

		if (!logicChain || logicChain.length === 0) {
			console.log(
				"⚠ No logic chain available, falling back to simple UNION of selected segments",
			);
			return null;
		}

		if (selectedSegments.length === 0) {
			console.log("⚠ No segments selected");
			return null;
		}

		// Get details for all selected segments with their search term mapping
		const selectedSegmentDetails = selectedSegments.map((segmentId) => {
			const details = getSegmentDetails(segmentId);

			// Find which search term this segment belongs to
			let belongsToTerm = null;
			for (const [searchKey, data] of Object.entries(segmentsData)) {
				if (data && data.data) {
					const found = data.data.find(
						(s) => s.segmentVector?.segmentId === segmentId,
					);
					if (found) {
						// Extract term name from search key (e.g., "SearchTerm_Cars" -> "cars")
						belongsToTerm = searchKey
							.toLowerCase()
							.replace("searchterm_", "")
							.replace("demographic_", "");
						break;
					}
				}
			}

			return {
				id: segmentId,
				calculatedReach: details.calculatedReach, // Use calculatedReach instead of reach
				belongsToTerm: belongsToTerm,
				segmentData: details,
			};
		});

		console.log(
			"📊 Selected segments with term mapping:",
			selectedSegmentDetails,
		);

		// Group selected segments by their search terms and logic
		const termGroups = new Map();

		// Process logic chain to understand grouping
		logicChain.forEach((item, index) => {
			const termText = item.term.text.toLowerCase();
			const termType = item.term.type;
			const operator = item.operator;

			if (termType === "include") {
				// Find selected segments that belong to this search term
				const termSegments = selectedSegmentDetails.filter(
					(seg) => seg.belongsToTerm && seg.belongsToTerm.includes(termText),
				);

				if (termSegments.length > 0) {
					if (!termGroups.has(termText)) {
						termGroups.set(termText, {
							segments: [],
							operator: operator,
							type: "include",
						});
					}

					// Add segments to this term group - using calculatedReach
					termGroups.get(termText).segments.push(
						...termSegments.map((seg) => ({
							id: seg.id,
							reach: seg.calculatedReach, // Use calculatedReach for ORF calculation
						})),
					);
				}
			}
		});

		console.log(
			"🗂️ Grouped selected segments by terms:",
			Array.from(termGroups.entries()),
		);

		// If no term grouping found, treat all selected segments as one UNION group
		if (termGroups.size === 0) {
			console.log(
				"⚠️ No term grouping found, using all selected segments in UNION",
			);
			return {
				expr: {
					op: "UNION",
					children: selectedSegmentDetails.map((seg) => ({
						id: seg.id,
						reach: seg.calculatedReach, // Use calculatedReach
					})),
				},
			};
		}

		// Build the tree structure based on logic chain operators
		const includeGroups = Array.from(termGroups.values()).filter(
			(group) => group.type === "include",
		);

		let expression;

		if (includeGroups.length === 1) {
			// Single group - use UNION for segments within the group
			const group = includeGroups[0];
			if (group.segments.length === 1) {
				expression = group.segments[0];
			} else {
				expression = {
					op: "UNION",
					children: group.segments,
				};
			}
		} else {
			// Multiple groups - determine if they should be INTERSECTION or UNION based on operators
			const hasAndOperator = logicChain.some((item) => item.operator === "AND");

			const children = includeGroups.map((group) => {
				if (group.segments.length === 1) {
					return group.segments[0];
				} else {
					return {
						op: "UNION",
						children: group.segments,
					};
				}
			});

			expression = {
				op: hasAndOperator ? "INTERSECTION" : "UNION",
				children: children,
			};
		}

		const payload = {
			expr: expression,
		};

		console.log(
			"✅ Generated logic chain payload for SELECTED segments:",
			JSON.stringify(payload, null, 2),
		);
		console.log(
			"📈 Total segments in payload:",
			countSegmentsInPayload(payload),
		);

		return payload;
	};

	// Extract demographic filter data from filters prop
	const getDemographicFilterData = () => {
		const demographics = {};

		// Get demographic filters from the filters prop (from useSegments hook)
		if (filters.demographicFilters) {
			const demoFilters = filters.demographicFilters;

			// Age demographics
			if (demoFilters.ageRange) {
				demographics.age = {
					applied: true,
					range: demoFilters.ageRange,
					display: `${demoFilters.ageRange.start}-${demoFilters.ageRange.end}`,
					probability:
						segmentsData["Demographic_Age"]?.demographicProbabilities?.[
							"Demographic_Age"
						] || 1,
				};
			}

			// Location demographics
			if (demoFilters.location && demoFilters.location !== "US") {
				demographics.location = {
					applied: true,
					value: demoFilters.location,
					display: demoFilters.location,
					probability:
						segmentsData["Demographic_Location"]?.demographicProbabilities?.[
							"Demographic_Location"
						] || 1,
				};
			} else if (demoFilters.location === "US") {
				demographics.location = {
					applied: true,
					value: "US",
					display: "United States",
					probability:
						segmentsData["Demographic_Location"]?.demographicProbabilities?.[
							"Demographic_Location"
						] || 1,
				};
			}

			// Gender demographics
			if (demoFilters.gender) {
				demographics.gender = {
					applied: true,
					value: demoFilters.gender,
					display: demoFilters.gender,
					probability:
						segmentsData["Demographic_Gender"]?.demographicProbabilities?.[
							"Demographic_Gender"
						] || 1,
				};
			}
		}

		// Fallback to segmentsData if no filters available
		if (Object.keys(demographics).length === 0) {
			const dataSource =
				Object.keys(demographicData).length > 0
					? demographicData
					: segmentsData;

			// Age demographics
			if (dataSource["Demographic_Age"]) {
				const ageData = dataSource["Demographic_Age"];
				demographics.age = {
					applied: true,
					display: "Applied",
					probability:
						ageData.demographicProbabilities?.["Demographic_Age"] || 1,
					filters: ageData.selectedFilters || ageData.data || [],
					totalCount: ageData.totalCount || 0,
				};
			}

			// Location demographics
			if (dataSource["Demographic_Location"]) {
				const locationData = dataSource["Demographic_Location"];
				demographics.location = {
					applied: true,
					display: "Applied",
					probability:
						locationData.demographicProbabilities?.["Demographic_Location"] ||
						1,
					filters: locationData.selectedFilters || locationData.data || [],
					totalCount: locationData.totalCount || 0,
				};
			}

			// Gender demographics
			if (dataSource["Demographic_Gender"]) {
				const genderData = dataSource["Demographic_Gender"];
				demographics.gender = {
					applied: true,
					display: "Applied",
					probability:
						genderData.demographicProbabilities?.["Demographic_Gender"] || 1,
					filters: genderData.selectedFilters || genderData.data || [],
					totalCount: genderData.totalCount || 0,
				};
			}
		}

		return demographics;
	};

	const demographicFilters = getDemographicFilterData();

	// Calculate effective reach with demographic filters
	const calculateEffectiveReachWithDemographics = () => {
		if (!analysisData || !analysisData.finalReach) return 0;

		const finalReach = analysisData.finalReach;
		let totalProbability = 1;

		// Multiply all demographic probabilities
		Object.values(demographicFilters).forEach((filter) => {
			if (filter.probability) {
				totalProbability *= filter.probability;
			}
		});

		return finalReach * totalProbability;
	};

	// Handle sign in
	const handleSignIn = async () => {
		try {
			setAuthError(false);
			const result = await apiService.signIn();
			if (result.success) {
				// Auto-calculate if segments are selected
				if (selectedSegments.length > 0 || selectedSets.length > 0) {
					handleCalculateReach();
				}
			} else {
				setAuthError(true);
				setError("Sign-in failed. Please try again.");
			}
		} catch (err) {
			setAuthError(true);
			setError("Sign-in failed: " + err.message);
		}
	};

	// Get segment details for display - Updated to use calculatedReach
	const getSegmentDetails = (segmentId) => {
		// Search through all segmentsData to find the segment
		for (const [searchTerm, data] of Object.entries(segmentsData)) {
			if (data && data.data) {
				const segment = data.data.find(
					(s) => s.segmentVector?.segmentId === segmentId,
				);
				if (segment) {
					return {
						id: segmentId,
						name: segment.segmentVector?.segmentName || "Unknown Segment",
						calculatedReach:
							segment.segmentMetrics?.calculatedReach ||
							segment.segmentMetrics?.reach ||
							0, // Prefer calculatedReach
						reach: segment.segmentMetrics?.reach || 0, // Keep reach as fallback
						ecpm: segment.segmentMetrics?.avgEcpm || 0,
						score: segment.compositeScore || 0,
					};
				}
			}
		}
		return {
			id: segmentId,
			name: "Unknown Segment",
			calculatedReach: 0,
			reach: 0,
			ecpm: 0,
			score: 0,
		};
	};

	// Get set details for display - Updated to use calculatedReach from segments
	const getSetDetails = (setId) => {
		// Remove tab prefix if present (e.g., "SearchTerm_Cars_Performance" -> "Performance")
		const cleanSetId = setId.includes("_") ? setId.split("_").pop() : setId;

		// Search through cumulativeData
		for (const [key, data] of Object.entries(cumulativeData)) {
			if (key === cleanSetId || setId.includes(key)) {
				// Calculate total reach from individual segments if segment IDs are available
				let totalCalculatedReach = data.reach || 0;

				if (data.segmentIds) {
					const segmentIds = data.segmentIds.split(",").map((id) => id.trim());
					totalCalculatedReach = segmentIds.reduce((total, segmentId) => {
						const segmentDetails = getSegmentDetails(segmentId);
						return (
							total +
							(segmentDetails.calculatedReach || segmentDetails.reach || 0)
						);
					}, 0);
				}

				return {
					id: setId,
					name: data.name || key.replace(/([A-Z])/g, " $1").trim() + " Set",
					calculatedReach: totalCalculatedReach, // Use calculated reach from individual segments
					reach: data.reach || 0, // Keep original reach as fallback
					ecpm: data.avgEcpm || 0,
					segmentCount: data.noOfSegments || 0,
				};
			}
		}

		return {
			id: setId,
			name: "Unknown Set",
			calculatedReach: 0,
			reach: 0,
			ecpm: 0,
			segmentCount: 0,
		};
	};

	const handleCalculateReach = async () => {
		setLoading(true);
		setError(null);
		setAuthError(false);

		try {
			// Check authentication before proceeding
			if (!isAuthenticated) {
				setAuthError(true);
				setError("Please sign in to calculate reach analysis");
				return;
			}

			// Validate that we have selected items
			const totalSelectedItems = selectedSegments.length + selectedSets.length;
			if (totalSelectedItems === 0) {
				throw new Error("No segments or sets selected for analysis");
			}

			console.log("🎯 Starting reach calculation with:", {
				selectedSegments: selectedSegments.length,
				selectedSets: selectedSets.length,
				hasLogicChain: logicChain && logicChain.length > 0,
			});

			let customPayload = null;

			// Try to build logic chain payload for SELECTED segments only
			if (logicChain && logicChain.length > 0 && selectedSegments.length > 0) {
				console.log("🔄 Building logic chain payload for selected segments...");
				customPayload = buildLogicChainPayloadFromSelected();

				if (customPayload) {
					console.log("✅ Using logic chain payload with selected segments");
				}
			}

			// If no custom payload or logic chain, fall back to simple approach
			if (!customPayload) {
				console.log("🔄 Using fallback: simple segment + set selection");

				// Prepare segments with ID and calculatedReach for direct selections
				const directSegments = selectedSegments.map((segmentId) => {
					const details = getSegmentDetails(segmentId);
					return {
						id: segmentId,
						calculatedReach: details.calculatedReach || details.reach,
					};
				});

				// Prepare segments from sets
				const setSegments = [];
				selectedSets.forEach((setId) => {
					const cleanSetId = setId.includes("_")
						? setId.split("_").pop()
						: setId;

					// Find the set in cumulativeData and get its segment IDs
					for (const [key, data] of Object.entries(cumulativeData)) {
						if (key === cleanSetId || setId.includes(key)) {
							if (data.segmentIds) {
								const ids = data.segmentIds.split(",").map((id) => id.trim());
								ids.forEach((id) => {
									const segmentDetails = getSegmentDetails(id);
									setSegments.push({
										id: id,
										calculatedReach:
											segmentDetails.calculatedReach || segmentDetails.reach,
									});
								});
							}
							break;
						}
					}
				});

				// Combine all segments - map calculatedReach to reach for API compatibility
				const allSegments = [...directSegments, ...setSegments].map(
					(segment) => ({
						id: segment.id,
						reach: segment.calculatedReach, // API expects 'reach' field
					}),
				);

				if (allSegments.length === 0) {
					throw new Error("No valid segments found for analysis");
				}

				console.log(
					"📊 Fallback segments for ORF calculation:",
					allSegments.length,
					"segments",
				);
				console.log("📊 Sample segment data:", allSegments.slice(0, 3));

				// Use simple calculation
				const result = await calculateORF(allSegments);

				// Validate the result structure
				if (!result || typeof result !== "object") {
					throw new Error("Invalid response from ORF calculation API");
				}

				// Check if we have the expected structure
				if (!result.reachTree && !result.finalReach) {
					console.error("Unexpected API response structure:", result);
					throw new Error(
						"API response missing expected reachTree or finalReach data",
					);
				}

				setAnalysisData(result);
			} else {
				// Use custom logic chain payload
				console.log("🎯 Using logic chain payload for ORF calculation");

				// Import the enhanced calculation function
				const { calculateORFWithCustomPayload } = await import(
					"./logicChainConverter"
				);
				const result = await calculateORFWithCustomPayload(
					customPayload,
					apiService,
				);

				// Validate the result structure
				if (!result || typeof result !== "object") {
					throw new Error("Invalid response from ORF calculation API");
				}

				// Check if we have the expected structure
				if (!result.reachTree && !result.finalReach) {
					console.error("Unexpected API response structure:", result);
					throw new Error(
						"API response missing expected reachTree or finalReach data",
					);
				}

				setAnalysisData(result);
			}
		} catch (err) {
			console.error("⚠️ Calculate reach error:", err);

			// Handle authentication errors
			if (
				err.message.includes("Authentication") ||
				err.message.includes("sign in")
			) {
				setAuthError(true);
			}

			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	// Auto-calculate when panel opens and user is authenticated
	React.useEffect(() => {
		if (
			isOpen &&
			(selectedSegments.length > 0 || selectedSets.length > 0) &&
			isAuthenticated
		) {
			handleCalculateReach();
		}
	}, [isOpen, selectedSegments, selectedSets, isAuthenticated]);

	// Listen for authentication changes
	React.useEffect(() => {
		const unsubscribe = apiService.onTokenChange((event, authState) => {
			if (event === "cleared") {
				setAuthError(true);
				setError("Session expired. Please sign in again.");
				setAnalysisData(null);
			} else if (event === "stored" && authState.isAuthenticated) {
				setAuthError(false);
				setError(null);
			}
		});

		return unsubscribe;
	}, []);

	if (!isOpen) return null;

	const totalSelectedItems = selectedSegments.length + selectedSets.length;

	return (
		<div className="campaign-panel-overlay">
			<div className="campaign-panel">
				<div className="panel-header">
					<div className="header-content">
						<h2>Campaign Reach Analysis</h2>
						<p className="header-subtitle">
							Analyzing {totalSelectedItems} selected item
							{totalSelectedItems !== 1 ? "s" : ""}
						</p>
					</div>
					<button onClick={onClose} className="close-btn">
						<X className="w-5 h-5" />
					</button>
				</div>

				<div className="panel-body">
					{/* 1. AUDIENCE FORECAST - Simplified to show only Final Reach */}
					{analysisData && analysisData.reachTree && (
						<div className="section">
							<h3 className="section-title">Audience Forecast</h3>
							<div className="reach-card">
								<MetricsTooltip
									orf={analysisData.reachTree.orf}
									stdDev={analysisData.reachTree.stdDev}
									rawReach={analysisData.reachTree.rawReach}
								>
									<div className="reach-content">
										<span className="reach-label">
											Estimated Reach without demographic filters
										</span>
										<div className="reach-value-container">
											<span className="reach-value">
												{formatNumber(analysisData.finalReach)}
											</span>
											<span className="reach-bounds">
												[
												{formatNumber(
													Math.max(
														0,
														analysisData.reachTree.effectiveReach *
															(1 - analysisData.reachTree.stdDev),
													),
												)}{" "}
												-{" "}
												{formatNumber(
													analysisData.reachTree.effectiveReach +
														analysisData.reachTree.stdDev *
															analysisData.reachTree.effectiveReach,
												)}
												]
											</span>
										</div>
									</div>
								</MetricsTooltip>
							</div>
						</div>
					)}

					{/* 2. DEMOGRAPHIC FILTERS - Enhanced with effective reach */}
					{Object.keys(demographicFilters).length > 0 && (
						<div className="section">
							<h3 className="section-title">Applied Demographic Filters</h3>

							{/* Effective Reach with Demographics */}
							{analysisData && analysisData.reachTree && (
								<div className="demographic-reach-card">
									<div className="demographic-reach-content">
										<span className="demographic-reach-label">
											Effective Reach with Demographics
										</span>
										<span className="demographic-reach-value">
											{formatNumber(calculateEffectiveReachWithDemographics())}
										</span>
										<span className="reach-bounds">
											[
											{formatNumber(
												Math.max(
													0,
													calculateEffectiveReachWithDemographics() *
														(1 - analysisData.reachTree.stdDev),
												),
											)}{" "}
											-{" "}
											{formatNumber(
												calculateEffectiveReachWithDemographics() +
													analysisData.reachTree.stdDev *
														calculateEffectiveReachWithDemographics(),
											)}
											]
										</span>
									</div>
								</div>
							)}

							<div className="demographic-filters-grid">
								{demographicFilters.age &&
									demographicFilters.age.display !== "-" &&
									demographicFilters.age.display !== "undefined-undefined" && (
										<div className="demographic-filter-card">
											<div className="filter-header">
												<h5 className="filter-type">Age Demographics</h5>
												<span className="filter-value">
													{demographicFilters.age.display}
												</span>
												<span className="filter-probability">
													Probability Factor:{" "}
													{formatNumber(
														demographicFilters.age.probability,
														true,
													)}
												</span>

												{/* Top Age Segments */}
												<div className="top-segments-section">
													<span className="top-segments-title">
														Top 5 of {getTotalSegmentCount("Age")} Age Segments:
													</span>
													{getTopSegmentsByType("Age").map((segment, index) => (
														<div key={index} className="top-segment-item">
															<span className="segment-rank">#{index + 1}</span>
															<span
																className="segment-name"
																title={segment.name}
															>
																{segment.name}
															</span>
															<span className="segment-reach">
																{formatNumber(segment.calculatedReach)}
															</span>
														</div>
													))}
												</div>
											</div>
										</div>
									)}

								{demographicFilters.location &&
									getTotalSegmentCount("Location") > 0 && (
										<div className="demographic-filter-card">
											<div className="filter-header">
												<h5 className="filter-type">Location</h5>
												<span className="filter-value">
													{formatLocationDisplay(demographicFilters.location)}
												</span>
												<span className="filter-probability">
													Probability Factor:{" "}
													{formatNumber(
														demographicFilters.location.probability,
														true,
													)}
												</span>

												{/* Top Location Segments */}
												<div className="top-segments-section">
													<span className="top-segments-title">
														Top 5 of {getTotalSegmentCount("Location")} Location
														Segments:
													</span>
													{getTopSegmentsByType("Location").map(
														(segment, index) => (
															<div key={index} className="top-segment-item">
																<span className="segment-rank">
																	#{index + 1}
																</span>
																<span
																	className="segment-name"
																	title={segment.name}
																>
																	{segment.name}
																</span>
																<span className="segment-reach">
																	{formatNumber(segment.calculatedReach)}
																</span>
															</div>
														),
													)}
												</div>
											</div>
										</div>
									)}

								{demographicFilters.gender && (
									<div className="demographic-filter-card">
										<div className="filter-header">
											<h5 className="filter-type">Gender</h5>
											<span className="filter-value">
												{demographicFilters.gender.display}
											</span>
											<span className="filter-probability">
												Probability Factor:{" "}
												{formatNumber(
													demographicFilters.gender.probability,
													true,
												)}
											</span>

											{/* Top Gender Segments */}
											<div className="top-segments-section">
												<span className="top-segments-title">
													Top 5 of {getTotalSegmentCount("Gender")} Gender
													Segments:
												</span>
												{getTopSegmentsByType("Gender").map(
													(segment, index) => (
														<div key={index} className="top-segment-item">
															<span className="segment-rank">#{index + 1}</span>
															<span
																className="segment-name"
																title={segment.name}
															>
																{segment.name}
															</span>
															<span className="segment-reach">
																{formatNumber(segment.calculatedReach)}
															</span>
														</div>
													),
												)}
											</div>
										</div>
									</div>
								)}
							</div>
						</div>
					)}

					{/* 3. LOADING/ERROR STATES */}
					{loading && (
						<div className="section">
							<div className="loading-state">
								<div className="loading-spinner"></div>
								<p>Calculating reach overlap and frequency...</p>
							</div>
						</div>
					)}

					{error && (
						<div className="section">
							<div className={`error-state ${authError ? "auth-error" : ""}`}>
								<h3>
									{authError ? "Authentication Required" : "Analysis Error"}
								</h3>
								<p>{error}</p>
								{authError ? (
									<button onClick={handleSignIn} className="auth-btn">
										Sign In
									</button>
								) : (
									<button onClick={handleCalculateReach} className="retry-btn">
										Retry Analysis
									</button>
								)}
							</div>
						</div>
					)}

					{/* 4. SELECTED ITEMS SUMMARY - Moved to bottom */}
					<div className="section">
						{totalSelectedItems > 0 ? (
							<div className="selected-items-enhanced">
								{/* Show logic chain summary if available */}
								{logicChain &&
									logicChain.length > 0 &&
									selectedSegments.length > 0 && (
										<div className="logic-summary">
											<h4>Search Logic Applied:</h4>
											<div className="logic-chain-display">
												{logicChain.map((item, index) => (
													<span key={index} className="logic-item">
														<span className={`logic-term ${item.term.type}`}>
															{item.term.type === "include" ? "+" : "-"}
															{item.term.text}
														</span>
														{item.operator && (
															<span
																className={`logic-operator ${item.operator.toLowerCase()}`}
															>
																{item.operator}
															</span>
														)}
													</span>
												))}
											</div>
										</div>
									)}

								{/* Selected Segments - Grouped by Search Terms if logic chain exists */}
								{selectedSegments.length > 0 && (
									<div className="selected-segments-section">
										<h4>Individual Segments ({selectedSegments.length})</h4>
										{(() => {
											// Group segments by search terms if logic chain exists
											if (logicChain && logicChain.length > 0) {
												const groupedSegments = new Map();

												selectedSegments.forEach((segmentId) => {
													const details = getSegmentDetails(segmentId);

													// Find which search term this segment belongs to
													let belongsToTerm = "Unknown";
													for (const [searchKey, data] of Object.entries(
														segmentsData,
													)) {
														if (data && data.data) {
															const found = data.data.find(
																(s) => s.segmentVector?.segmentId === segmentId,
															);
															if (found) {
																belongsToTerm = searchKey
																	.replace("SearchTerm_", "")
																	.replace("Demographic_", "")
																	.replace(/_/g, " ");
																break;
															}
														}
													}

													if (!groupedSegments.has(belongsToTerm)) {
														groupedSegments.set(belongsToTerm, []);
													}

													groupedSegments.get(belongsToTerm).push({
														id: segmentId,
														name: details.name,
														calculatedReach: details.calculatedReach, // Use calculatedReach for display
													});
												});

												return (
													<div className="segments-by-term">
														{Array.from(groupedSegments.entries()).map(
															([termName, segments]) => {
																const groupReach =
																	findGroupEffectiveReach(segments);

																return (
																	<div key={termName} className="term-group">
																		<div className="term-group-header">
																			<span className="term-name">
																				{termName}
																			</span>
																			<span className="term-count">
																				({segments.length} segments)
																			</span>
																		</div>

																		{/* Show UNION operation and effective reach */}
																		{groupReach && (
																			<div className="union-operation">
																				<div className="operation-label">
																					<span className="operator-badge union">
																						UNION
																					</span>
																					<span className="operation-description">
																						Combined reach of {segments.length}{" "}
																						segments
																					</span>
																				</div>
																				<div className="operation-metrics">
																					<div className="operation-metric">
																						<span className="metric-label">
																							Effective Reach:
																						</span>
																						<span className="metric-value">
																							{formatNumber(
																								groupReach.effectiveReach,
																							)}
																						</span>
																					</div>
																					<div className="operation-metric">
																						<span className="metric-label">
																							ORF:
																						</span>
																						<span className="metric-value">
																							{groupReach.orf.toFixed(3)}
																						</span>
																					</div>
																				</div>
																			</div>
																		)}

																		<div className="term-segments">
																			{segments.map((segment) => (
																				<div
																					key={segment.id}
																					className="selected-item-row grouped"
																				>
																					<span
																						className="item-name"
																						title={segment.name}
																					>
																						{segment.name}
																					</span>
																					<span className="item-reach">
																						{formatNumber(
																							segment.calculatedReach,
																						)}{" "}
																						users
																					</span>
																				</div>
																			))}
																		</div>
																	</div>
																);
															},
														)}
													</div>
												);
											} else {
												// No logic chain - show flat list
												return (
													<div className="selected-items-compact">
														{selectedSegments.map((segmentId) => {
															const details = getSegmentDetails(segmentId);
															return (
																<div
																	key={segmentId}
																	className="selected-item-row"
																>
																	<span
																		className="item-name"
																		title={details.name}
																	>
																		{details.name}
																	</span>
																	<span className="item-reach">
																		{formatNumber(details.calculatedReach)}{" "}
																		users
																	</span>
																</div>
															);
														})}
													</div>
												);
											}
										})()}
									</div>
								)}

								{/* Selected Sets */}
								{selectedSets.length > 0 && (
									<div className="selected-sets-section">
										<h4>Performance Sets ({selectedSets.length})</h4>
										<div className="selected-items-compact">
											{selectedSets.map((setId) => {
												const details = getSetDetails(setId);
												return (
													<div key={setId} className="selected-item-row">
														<span className="item-name" title={details.name}>
															{details.name} ({details.segmentCount} segments)
														</span>
														<span className="item-reach">
															{formatNumber(details.calculatedReach)} users
														</span>
													</div>
												);
											})}
										</div>
									</div>
								)}
							</div>
						) : (
							<div className="no-selection">
								<p>No segments or sets selected for analysis</p>
							</div>
						)}
					</div>
				</div>

				<div className="panel-footer">
					<button onClick={onClose} className="secondary-btn">
						Close
					</button>
					{!loading &&
						!analysisData &&
						isAuthenticated &&
						totalSelectedItems > 0 && (
							<button onClick={handleCalculateReach} className="primary-btn">
								Calculate Reach
							</button>
						)}
					{analysisData && totalSelectedItems > 0 && (
						<button onClick={handleCalculateReach} className="primary-btn">
							Recalculate
						</button>
					)}
				</div>
			</div>

			<style jsx>{`
        .campaign-panel-overlay {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1000;
          display: flex;
          justify-content: flex-end;
          backdrop-filter: blur(2px);
        }

        .campaign-panel {
          background: white;
          width: 600px;
          max-width: 90vw;
          height: 100vh;
          overflow-y: auto;
          box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
          display: flex;
          flex-direction: column;
          transform: translateX(100%);
          animation: slideIn 0.3s ease-out forwards;
        }

        @keyframes slideIn {
          to {
            transform: translateX(0);
          }
        }

        .panel-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 24px;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .header-content h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
        }

        .header-subtitle {
          margin: 4px 0 0 0;
          font-size: 14px;
          color: #6b7280;
        }

        .close-btn {
          background: #f3f4f6;
          border: none;
          color: #6b7280;
          cursor: pointer;
          padding: 8px;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .close-btn:hover {
          background: #e5e7eb;
          color: #374151;
        }

        .panel-body {
          flex: 1;
          padding: 24px;
          overflow-y: auto;
        }

        .section {
          margin-bottom: 32px;
        }

        .section:last-child {
          margin-bottom: 0;
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
          font-size: 16px;
          font-weight: 600;
          color: #374151;
        }

        .union-operation {
  margin: 12px 0;
  padding: 12px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #0891b2;
  border-radius: 6px;
}

.operation-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.operator-badge {
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.operator-badge.union {
  background: #0891b2;
  color: white;
}

.operation-description {
  font-size: 12px;
  color: #0f766e;
  font-weight: 500;
}

.operation-metrics {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.operation-metric {
  display: flex;
  align-items: center;
  gap: 4px;
}

.operation-metric .metric-label {
  font-size: 11px;
  color: #0f766e;
  font-weight: 500;
}

.operation-metric .metric-value {
  font-size: 12px;
  color: #0f172a;
  font-weight: 600;
  background: white;
  padding: 2px 6px;
  border-radius: 3px;
  border: 1px solid #cbd5e1;
}

        .reach-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 20px;
          transition: all 0.2s;
        }

        .reach-card:hover {
          border-color: #d1d5db;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .tooltip-container {
          position: relative;
          display: inline-block;
          width: 100%;
        }

        .tooltip-trigger {
          display: flex;
          align-items: center;
          width: 100%;
        }

        .reach-content {
          display: flex;
          flex-direction: column;
          gap: 8px;
          flex: 1;
        }

        .reach-label {
          font-size: 14px;
          color: #6b7280;
          font-weight: 500;
        }

        .reach-value-container {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .reach-value {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          line-height: 1;
        }

        .reach-bounds {
          font-size: 13px;
          color: #6b7280;
          font-weight: 400;
        }

        .tooltip-content {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          padding: 12px;
          z-index: 1001;
          margin-top: 8px;
        }

        .tooltip-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 0;
          border-bottom: 1px solid #f3f4f6;
        }

        .tooltip-item:last-child {
          border-bottom: none;
        }

        .tooltip-label {
          font-size: 13px;
          color: #6b7280;
          font-weight: 500;
        }

        .tooltip-value {
          font-size: 13px;
          color: #1f2937;
          font-weight: 600;
        }

        .demographic-reach-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
        }

        .demographic-reach-content {
          display: flex;
          flex-direction: column;
          gap: 6px;
          text-align: left;
        }

        .demographic-reach-label {
          font-size: 14px;
          color: #6b7280;
          font-weight: 500;
        }

        .demographic-reach-value {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
        }

        .selected-category {
          margin-bottom: 20px;
        }

        .selected-category h4 {
          margin: 0 0 12px 0;
          font-size: 14px;
          font-weight: 500;
          color: #6b7280;
        }

        .selected-items-compact {
          display: flex;
          flex-direction: column;
          gap: 4px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 12px;
          max-height: 200px;
          overflow-y: auto;
        }

        .selected-item-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 8px;
          background: white;
          border-radius: 4px;
          border: 1px solid #e5e7eb;
          transition: all 0.2s;
        }

        .selected-item-row:hover {
          border-color: #3b82f6;
          box-shadow: 0 1px 3px rgba(59, 130, 246, 0.1);
        }

        .selected-item-row .item-name {
          font-weight: 500;
          color: #1f2937;
          font-size: 13px;
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          margin-right: 12px;
        }

        .selected-item-row .item-reach {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
          flex-shrink: 0;
        }

        .no-selection {
          text-align: center;
          padding: 20px;
          color: #6b7280;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 14px;
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
        }

        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
          color: #dc2626;
        }

        .error-state.auth-error {
          color: #d97706;
          background: #fffbeb;
          border: 1px solid #fed7aa;
          border-radius: 8px;
        }

        .error-state h3 {
          margin: 0 0 8px 0;
          color: #dc2626;
        }

        .error-state.auth-error h3 {
          color: #d97706;
        }

        .error-state p {
          margin: 0 0 16px 0;
          color: #6b7280;
        }

        .retry-btn {
          padding: 8px 16px;
          background: #dc2626;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
        }

        .retry-btn:hover {
          background: #b91c1c;
        }

        .auth-btn {
          padding: 8px 16px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          font-size: 14px;
          transition: all 0.2s;
        }

        .auth-btn:hover {
          background: #2563eb;
        }

        .panel-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 24px;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .secondary-btn {
          padding: 10px 20px;
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }

        .secondary-btn:hover {
          background: #e5e7eb;
          border-color: #9ca3af;
        }

        .primary-btn {
          padding: 10px 20px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }

        .primary-btn:hover {
          background: #2563eb;
        }

        .logic-summary {
          margin-bottom: 20px;
          padding: 16px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
        }

        .logic-summary h4 {
          margin: 0 0 12px 0;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
        }

        .logic-chain-display {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
        }

        .logic-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .logic-term {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
        }

        .logic-term.include {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #bbf7d0;
        }

        .logic-term.exclude {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        .logic-operator {
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .logic-operator.and {
          background: #dbeafe;
          color: #1e40af;
        }

        .logic-operator.or {
          background: #fed7aa;
          color: #c2410c;
        }

        .logic-operator.not {
          background: #fee2e2;
          color: #dc2626;
        }

        .selected-segments-section,
        .selected-sets-section {
          margin-bottom: 20px;
        }

        .selected-segments-section h4,
        .selected-sets-section h4 {
          margin: 0 0 12px 0;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
        }

        .segments-by-term {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .term-group {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 12px;
        }

        .term-group-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .term-name {
          font-weight: 600;
          color: #374151;
          font-size: 13px;
        }

        .term-count {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
        }

        .term-segments {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .selected-item-row.grouped {
          background: white;
          border: 1px solid #e5e7eb;
          margin: 0;
        }

        .demographic-filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }

        .demographic-filter-card {
          padding: 16px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
        }

        .filter-header {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .filter-type {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
        }

        .filter-value {
          font-size: 13px;
          color: #1f2937;
          font-weight: 500;
          background: #e0f2fe;
          padding: 4px 8px;
          border-radius: 4px;
          border: 1px solid #0891b2;
          display: inline-block;
          max-width: fit-content;
        }

        .filter-probability {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
        }

        .top-segments-section {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
}

.top-segments-title {
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
  display: block;
  margin-bottom: 6px;
}

.top-segment-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  padding: 2px 0;
}

.segment-rank {
  color: #9ca3af;
  font-weight: 600;
  min-width: 18px;
  flex-shrink: 0;
}

.segment-name {
  color: #374151;
  font-weight: 500;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.segment-reach {
  color: #059669;
  font-weight: 600;
  font-size: 9px;
  flex-shrink: 0;
}

        @media (max-width: 768px) {
          .campaign-panel {
            width: 100vw;
            max-width: 100vw;
          }

          .reach-card {
            padding: 20px 16px;
          }

          .reach-value {
            font-size: 24px;
          }

          .panel-header {
            padding: 16px;
          }

          .panel-body {
            padding: 16px;
          }

          .panel-footer {
            padding: 16px;
          }

          .selected-items-compact {
            padding: 8px;
            max-height: 150px;
          }

          .selected-item-row {
            padding: 4px 6px;
          }

          .selected-item-row .item-name {
            font-size: 12px;
          }

          .selected-item-row .item-reach {
            font-size: 11px;
          }

          .demographic-filters-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .reach-card {
            padding: 16px 12px;
          }

          .reach-value {
            font-size: 16px;
          }

          .reach-bounds {
            font-size: 12px;
          }

          .demographic-reach-value {
            font-size: 16px;
          }

          .selected-items-compact {
            max-height: 120px;
          }
        }
      `}</style>
		</div>
	);
};
